import { Box, TextField, Typography, Link, Button, Container, Grid, InputAdornment, IconButton, Stack } from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createEmptyInstructor, Instructor, instructorToSignInPayload } from "../../models/instructor";
import Notifier from "../../components/notifier";
import { useSnackbar } from "../../components/snackbar";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const isPasswordValid = (password: string) => {
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d\S]{8,}$/.test(password);
};

function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [email, setEmail] = useState('');

    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    
    const navigate = useNavigate();

    const { showMessage } = useSnackbar();
    
    const [loading, setLoading] = useState(false);

    const fieldStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: 3,
            '&:hover fieldset': {
                borderColor: '#667eea'
            },
            '&.Mui-focused fieldset': {
                borderColor: '#667eea'
            }
        }
    };


    const handleInputChange = (value: string) => {
        setEmail(value);
    };
    
    const sendPasswordResetEmail = async () => {
        if (!email.trim()) {
            showMessage('enter email address', 0);
            return;
        }

        try {
            setLoading(true);
            await axios.post(
                `${API_URL}/auth/instructors/password-reset-email`, 
                { 'email': email }
            );
            showMessage('password reset link sent', 3);
        } catch (error: any) {
            showMessage(error.response?.data?.detail || 'an error occurred', 0)
        } finally {
            setLoading(false);
        }
    };

    const resetPassword = async () => {
        if (password !== passwordConfirm) {
            showMessage('passwords do not match', 0);
            return;
        }

        try {
            setLoading(true);
            await axios.put(
                `${API_URL}/auth/instructors/reset-password`, 
                {
                    'token': token, 
                    'password': password 
                }
            );
            showMessage('password reset', 3);
            navigate('/sign-in');
        } catch (error: any) {
            showMessage(error.response?.data?.detail || 'an error occurred', 0)
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex" }}>

            {/* Right Side - Sign In Form */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "grey.100",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 3,
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.5rem",
                            userSelect: "none",
                            fontFamily: "'Inter', 'Roboto', sans-serif",
                            color: "#3B82F6",
                            letterSpacing: "-0.02em"
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                color: "green",
                                fontWeight: 800,
                                fontSize: "1.6rem"
                            }}
                        >
                            e
                        </Box>
                        LMS
                        <Box
                            component="span"
                            sx={{
                                color: "green",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                ml: 1,
                                opacity: 0.7
                            }}
                        >
                            Instructor
                        </Box>
                    </Typography>
                </Box>

                {/* Form Container */}
                <Container
                    maxWidth="sm"
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        py: 4
                    }}
                >
                    <Box sx={{ maxWidth: 400, mx: "auto", width: "100%" }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                color: "#1a1a1a"
                            }}
                        >
                            Reset Password
                        </Typography>
                     

                        {!token ? (
                            <TextField
                                fullWidth
                                label="Email"
                                variant="outlined"
                                value={email}
                                onChange={(e) => handleInputChange(e.target.value)}
                                sx={fieldStyles}
                            />
                        ) : (
                            <Stack spacing={1}>
                                <TextField
                                    fullWidth
                                    label="Password"
                                    variant="outlined"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    error={(password && !isPasswordValid(password)) || false}
                                    helperText={password && !isPasswordValid(password) && "Password must be at least 8 characters with letters and numbers"}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    sx={fieldStyles}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Confirm Password"
                                    variant="outlined"
                                    type={showPasswordConfirm ? "text" : "password"}
                                    value={passwordConfirm}
                                    onChange={(e) => setPasswordConfirm(e.target.value)}
                                    // error={!!errors.password_repeat}
                                    // helperText={errors.password_repeat}
                                    placeholder="Confirm your password"
                                    sx={fieldStyles}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                                                    edge="end"
                                                >
                                                    {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>
                        )}

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={() => {
                                if (token) {
                                    resetPassword();
                                } else {
                                    sendPasswordResetEmail();
                                }
                            }}
                            disabled={loading}
                            sx={{
                                mt: 2,
                                py: 1.5,
                                borderRadius: 3,
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                                fontSize: "1rem",
                                fontWeight: 600,
                                textTransform: "none",
                                '&:hover': {
                                    boxShadow: "0 6px 25px rgba(102, 126, 234, 0.6)",
                                    transform: "translateY(-1px)"
                                },
                                transition: "all 0.3s ease"
                            }}
                        >
                            {token ? "Reset Password" : "Send Password Reset Link"}
                        </Button>

                      
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default ResetPassword;